/**
 * Handles importing validated flight patterns into the database
 * and generating the rolling daily cache.
 */

import { supabase } from "@/integrations/supabase/client";
import type { ValidatedFlightPatternRow, ScheduleHeader } from "@/utils/scheduleMarkdownParser";

export type ImportStep = 'idle' | 'patterns' | 'cache' | 'logging' | 'maintenance' | 'done' | 'error';

export interface ImportProgress {
  step: ImportStep;
  message: string;
  patternsImported?: number;
  cacheRowsGenerated?: number;
  rowsPruned?: number;
}

const SUPABASE_URL = "https://ejxokqjbhhxrwwvwkojd.supabase.co";
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImVqeG9rcWpiaGh4cnd3dndrb2pkIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDY1OTAxNDcsImV4cCI6MjA2MjE2NjE0N30.UN6sO9vPaAy_j3nSvbTe98Iki7s1OcZ0esgbUbt2fZs";

const PNG_UTC_OFFSET_HOURS = 10;

/**
 * Build a UTC datetime string from a local date (YYYY-MM-DD) and time (HH:MM) in PNG timezone (UTC+10).
 */
function toUtcDatetime(dateStr: string, timeStr: string): string {
  const [h, m] = timeStr.split(':').map(Number);
  const dt = new Date(`${dateStr}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00+10:00`);
  return dt.toISOString();
}

/**
 * Get the day-of-week bit index for a given date. 0=Sun, 1=Mon, ... 6=Sat.
 */
function getDayBit(date: Date): number {
  return date.getDay(); // JS getDay: 0=Sun, 1=Mon, ... 6=Sat — matches our bitmask
}

/**
 * Format a Date as YYYY-MM-DD.
 */
function formatDateStr(d: Date): string {
  return d.toISOString().split('T')[0];
}

/**
 * Add days to a date.
 */
function addDays(d: Date, n: number): Date {
  const result = new Date(d);
  result.setDate(result.getDate() + n);
  return result;
}

function deduplicateRows(rows: ValidatedFlightPatternRow[], scheduleVersion: string): ValidatedFlightPatternRow[] {
  const uniqueMap = new Map<string, ValidatedFlightPatternRow>();
  for (const row of rows) {
    const key = `${scheduleVersion}|${row.flight_number}|${row.dep_airport}|${row.dep_time}`;
    uniqueMap.set(key, row);
  }
  const deduplicated = Array.from(uniqueMap.values());
  const duplicateCount = rows.length - deduplicated.length;
  console.log(`Deduplication: ${rows.length} rows -> ${deduplicated.length} unique rows (${duplicateCount} duplicates removed)`);
  return deduplicated;
}

export async function importSchedule(
  validRows: ValidatedFlightPatternRow[],
  header: ScheduleHeader,
  airlineCode: string,
  scheduleVersion: string,
  onProgress: (progress: ImportProgress) => void,
  fileHash?: string
): Promise<ImportProgress> {
  try {
    // ─── Step 0: Deduplicate by conflict key ───
    const uniqueRows = deduplicateRows(validRows, scheduleVersion);

    // ─── Step 1: Upsert flight_patterns ───
    onProgress({ step: 'patterns', message: '⏳ Importing flight patterns...' });

    const patternPayloads = uniqueRows.map(row => ({
      airline_code: airlineCode,
      schedule_version: scheduleVersion,
      flight_number: row.flight_number,
      dep_airport: row.dep_airport,
      dep_time: row.dep_time,
      arr_airport: row.arr_airport,
      arr_time: row.arr_time,
      days_bitmask: row.days_bitmask,
      aircraft_type: row.aircraft_type || null,
      notes: row.notes || null,
      valid_from: header.valid_from,
      valid_to: header.valid_to,
      imported_at: new Date().toISOString(),
      file_hash: fileHash || null,
    }));

    // Upsert in batches of 50
    const BATCH_SIZE = 50;
    const upsertedPatternIds: number[] = [];

    for (let i = 0; i < patternPayloads.length; i += BATCH_SIZE) {
      const batch = patternPayloads.slice(i, i + BATCH_SIZE);
      const { data, error } = await supabase
        .from('flight_patterns')
        .upsert(batch as any, {
          onConflict: 'schedule_version,flight_number,dep_airport,dep_time',
        })
        .select('id');

      if (error) {
        throw new Error(`Pattern upsert failed: ${error.message}`);
      }
      if (data) {
        upsertedPatternIds.push(...data.map((d: any) => d.id));
      }
    }

    const patternsImported = upsertedPatternIds.length;
    onProgress({
      step: 'patterns',
      message: `✅ ${patternsImported} flight patterns saved`,
      patternsImported,
    });

    // ─── Step 2: Generate flight_daily_cache ───
    onProgress({ step: 'cache', message: '⏳ Generating daily cache...' });

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const rangeStart = addDays(today, -14);
    const rangeEnd = addDays(today, 30);

    // Delete existing cache for this schedule_version + airline_code in the date range
    const { error: deleteError } = await supabase
      .from('flight_daily_cache')
      .delete()
      .gte('dep_date', formatDateStr(rangeStart))
      .lte('dep_date', formatDateStr(rangeEnd))
      .in('pattern_id', upsertedPatternIds as any);

    if (deleteError) {
      throw new Error(`Cache cleanup failed: ${deleteError.message}`);
    }

    // Now we need to re-fetch the patterns to get their IDs mapped to the rows
    // Since we upserted, fetch all patterns for this schedule_version
    const { data: patterns, error: fetchError } = await supabase
      .from('flight_patterns')
      .select('id, flight_number, dep_airport, dep_time, arr_airport, arr_time, days_bitmask, aircraft_type, notes')
      .eq('schedule_version', scheduleVersion)
      .eq('airline_code', airlineCode);

    if (fetchError) {
      throw new Error(`Failed to fetch patterns: ${fetchError.message}`);
    }

    // Generate cache rows
    const cacheRows: any[] = [];
    const totalDays = Math.ceil((rangeEnd.getTime() - rangeStart.getTime()) / (1000 * 60 * 60 * 24));

    for (const pattern of (patterns || [])) {
      for (let d = 0; d <= totalDays; d++) {
        const currentDate = addDays(rangeStart, d);
        const dayBit = getDayBit(currentDate);

        // Check if this pattern runs on this day
        if (!(pattern.days_bitmask & (1 << dayBit))) continue;

        const depDateStr = formatDateStr(currentDate);
        const depTime = pattern.dep_time as string;
        const arrTime = pattern.arr_time as string;
        const isOvernight = arrTime < depTime;
        const arrDate = isOvernight ? addDays(currentDate, 1) : currentDate;
        const arrDateStr = formatDateStr(arrDate);

        cacheRows.push({
          pattern_id: pattern.id,
          flight_number: pattern.flight_number,
          dep_airport: pattern.dep_airport,
          arr_airport: pattern.arr_airport,
          dep_date: depDateStr,
          dep_datetime: toUtcDatetime(depDateStr, depTime),
          arr_date: arrDateStr,
          arr_datetime: toUtcDatetime(arrDateStr, arrTime),
          aircraft_type: pattern.aircraft_type,
          notes: pattern.notes,
          is_overnight: isOvernight,
        });
      }
    }

    // Bulk insert cache rows in batches
    let cacheRowsInserted = 0;
    for (let i = 0; i < cacheRows.length; i += BATCH_SIZE) {
      const batch = cacheRows.slice(i, i + BATCH_SIZE);
      const { error: insertError } = await supabase
        .from('flight_daily_cache')
        .insert(batch as any);

      if (insertError) {
        throw new Error(`Cache insert failed at batch ${Math.floor(i / BATCH_SIZE) + 1}: ${insertError.message}`);
      }
      cacheRowsInserted += batch.length;
    }

    // ─── Step 3: Log to admin_activity_log ───
    onProgress({ step: 'logging', message: '⏳ Logging import activity...' });

    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData?.session?.user?.id;

    if (userId) {
      await supabase.from('admin_activity_log').insert({
        user_id: userId,
        action: 'schedule_import',
        entity_type: 'flight_patterns',
        new_values: {
          airline_code: airlineCode,
          schedule_version: scheduleVersion,
          valid_from: header.valid_from,
          valid_to: header.valid_to,
          patterns_imported: patternsImported,
          cache_rows_generated: cacheRowsInserted,
        },
      } as any);
    }

    // ─── Step 4: Run cache maintenance ───
    onProgress({ step: 'maintenance', message: '⏳ Running cache maintenance...' });

    let rowsPruned = 0;
    let maintenanceFailed = false;
    let maintenanceErrorMsg = '';

    try {
      const maintenanceResult = await fetch(
        `${SUPABASE_URL}/functions/v1/maintain-flight-cache`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const maintenanceData = await maintenanceResult.json();

      if (!maintenanceResult.ok) {
        throw new Error(maintenanceData.error || 'Cache maintenance failed');
      }

      rowsPruned = maintenanceData.rowsPruned || 0;
    } catch (maintenanceError: unknown) {
      maintenanceFailed = true;
      maintenanceErrorMsg = maintenanceError instanceof Error ? maintenanceError.message : 'Unknown error';
      console.error('Cache maintenance error:', maintenanceError);
    }

    const finalMessage = maintenanceFailed
      ? `⚠️ Import succeeded but cache maintenance failed: ${maintenanceErrorMsg}\n\n✅ ${patternsImported} patterns saved, ${cacheRowsInserted} cache rows generated`
      : `✅ Import complete — ${patternsImported} patterns saved, ${cacheRowsInserted} cache rows generated, ${rowsPruned} stale rows pruned`;

    const finalProgress: ImportProgress = {
      step: 'done',
      message: finalMessage,
      patternsImported,
      cacheRowsGenerated: cacheRowsInserted,
      rowsPruned,
    };
    onProgress(finalProgress);
    return finalProgress;

  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'Unknown error';
    const errorProgress: ImportProgress = {
      step: 'error',
      message: `❌ ${msg}`,
    };
    onProgress(errorProgress);
    return errorProgress;
  }
}
